import { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const R = 1.6;

function latLngToVec3(lat, lng, radius) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

function Marker({ lat, lng }) {
  const pos = useMemo(() => latLngToVec3(lat, lng, R), [lat, lng]);
  const normal = useMemo(() => pos.clone().normalize(), [pos]);
  const beamRef = useRef();
  const ring1 = useRef();
  const ring2 = useRef();
  const q = useMemo(() => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal), [normal]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    [ring1, ring2].forEach((r, i) => {
      if (!r.current) return;
      const p = ((t * 0.7 + i * 0.5) % 1);
      r.current.scale.setScalar(0.2 + p * 1.6);
      r.current.material.opacity = 0.55 * (1 - p);
    });
  });

  const beamH = 1.1;
  return (
    <group>
      <group position={pos} quaternion={q}>
        <mesh ref={ring1} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]}>
          <ringGeometry args={[0.1, 0.13, 32]} />
          <meshBasicMaterial color="#38BDF8" transparent opacity={0.5} side={THREE.DoubleSide} />
        </mesh>
        <mesh ref={ring2} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]}>
          <ringGeometry args={[0.1, 0.13, 32]} />
          <meshBasicMaterial color="#0052FF" transparent opacity={0.5} side={THREE.DoubleSide} />
        </mesh>
        <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.05, 24]} />
          <meshBasicMaterial color="#0EA5E9" />
        </mesh>
        <mesh ref={beamRef} position={[0, beamH / 2, 0]}>
          <cylinderGeometry args={[0.014, 0.014, beamH, 8]} />
          <meshBasicMaterial color="#38BDF8" transparent opacity={0.65} />
        </mesh>
        <mesh position={[0, beamH + 0.02, 0]}>
          <sphereGeometry args={[0.045, 16, 16]} />
          <meshBasicMaterial color="#7DD3FC" />
        </mesh>
      </group>
    </group>
  );
}

function SignalArc({ from, to }) {
  const curve = useMemo(() => new THREE.QuadraticBezierCurve3(from, from.clone().add(to).multiplyScalar(0.5).normalize().multiplyScalar(R + 1.45), to), [from, to]);
  const line = useMemo(() => new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(curve.getPoints(48)),
    new THREE.LineDashedMaterial({ color: '#38BDF8', dashSize: 0.08, gapSize: 0.06, transparent: true, opacity: 0.55 })
  ), [curve]);
  useMemo(() => line.computeLineDistances(), [line]);
  const dot = useRef();
  useFrame(({ clock }) => {
    if (dot.current) {
      const p = curve.getPoint((clock.getElapsedTime() * 0.22) % 1);
      dot.current.position.copy(p);
    }
  });
  return (
    <group>
      <primitive object={line} />
      <mesh ref={dot}><sphereGeometry args={[0.035, 12, 12]} /><meshBasicMaterial color="#0052FF" /></mesh>
    </group>
  );
}

function Satellite({ radius = R + 1.35, speed = 0.5, tilt = 0.5 }) {
  const group = useRef();
  const core = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() * speed;
    if (group.current) {
      group.current.position.set(Math.cos(t) * radius, Math.sin(t) * radius * Math.sin(tilt), Math.sin(t) * radius * Math.cos(tilt));
      group.current.lookAt(0, 0, 0);
    }
    if (core.current) core.current.rotation.y += 0.02;
  });
  return (
    <group ref={group}>
      <mesh ref={core}>
        <boxGeometry args={[0.12, 0.18, 0.12]} />
        <meshStandardMaterial color="#0F172A" metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[0.2, 0, 0]}>
        <boxGeometry args={[0.24, 0.015, 0.11]} />
        <meshStandardMaterial color="#38BDF8" emissive="#38BDF8" emissiveIntensity={1} />
      </mesh>
      <mesh position={[-0.2, 0, 0]}>
        <boxGeometry args={[0.24, 0.015, 0.11]} />
        <meshStandardMaterial color="#38BDF8" emissive="#38BDF8" emissiveIntensity={1} />
      </mesh>
      <mesh position={[0, 0.14, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[0.07, 0.07, 16]} />
        <meshStandardMaterial color="#E2E8F0" metalness={0.3} roughness={0.5} />
      </mesh>
      <pointLight intensity={0.8} distance={3} color="#7DD3FC" />
    </group>
  );
}

function Earth() {
  const group = useRef();
  const rotTarget = useRef(0);
  const texture = useMemo(() => {
    const t = new THREE.TextureLoader().load('/textures/earth-blue-marble.jpg');
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, []);
  const iraqPos = useMemo(() => latLngToVec3(33.2, 43.7, R), []);
  const arcs = useMemo(() => {
    const a1 = latLngToVec3(33.2, 43.7, R + 1.3);
    const a2 = a1.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.7);
    const a3 = a1.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), -0.65);
    return [iraqPos.clone().normalize().multiplyScalar(R * 1.01), iraqPos.clone().normalize().multiplyScalar(R * 1.01)].map((p, i) => [p, i === 0 ? a2 : a3]);
  }, [iraqPos]);

  useMemo(() => {
    const theta = Math.atan2(iraqPos.z, iraqPos.x);
    rotTarget.current = theta - Math.PI / 2 - 0.7;
  }, [iraqPos]);

  useFrame((_, dt) => {
    if (!group.current) return;
    rotTarget.current += dt * 0.015;
    group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, rotTarget.current, 1 - Math.exp(-2 * dt));
  });

  return (
    <group ref={group} rotation={[0.28, 0, 0.12]}>
      <mesh>
        <sphereGeometry args={[R, 64, 64]} />
        <meshStandardMaterial map={texture} roughness={0.85} metalness={0.05} />
      </mesh>
      <mesh scale={1.09}>
        <sphereGeometry args={[R, 32, 32]} />
        <meshBasicMaterial color="#38BDF8" transparent opacity={0.07} side={THREE.BackSide} blending={THREE.AdditiveBlending} />
      </mesh>
      <Marker lat={33.2} lng={43.7} />
      {arcs.map((a, i) => <SignalArc key={i} from={a[0]} to={a[1]} />)}
      <Satellite />
    </group>
  );
}

function Stars() {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const arr = new Float32Array(60 * 3);
    for (let i = 0; i < 60; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 7;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 6 - 2;
    }
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    return g;
  }, []);
  return <points geometry={geometry}><pointsMaterial size={0.028} color="#0EA5E9" transparent opacity={0.5} sizeAttenuation /></points>;
}

export default function GlobeScene() {
  return (
    <div className="pointer-events-none absolute inset-0" data-testid="globe-scene">
      <Canvas camera={{ position: [0, 0.7, 4.8], fov: 42 }} gl={{ alpha: true, antialias: true }} dpr={[1, 1.75]} style={{ position: 'absolute', inset: 0 }}>
        <ambientLight intensity={1.3} />
        <directionalLight position={[4, 3, 5]} intensity={2.2} />
        <directionalLight position={[-4, -1, -3]} intensity={0.5} color="#7DD3FC" />
        <Earth />
        <Stars />
      </Canvas>
    </div>
  );
}
